import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { voice } from '@repletoryreach/sdk-api';

/**
 * Reach Voice, from the platform's side. Every write invalidates the whole
 * `['admin','voice']` subtree: the kill switch changes the settings, the
 * engines' live calls and the audit trail at once, and a page that shows
 * "on" above a trail that says "off" is the last thing an operator needs.
 */
const ROOT = ['admin', 'voice'] as const;

export function useVoiceSettings() {
  return useQuery({ queryKey: [...ROOT, 'settings'], queryFn: () => voice.adminGetSettings() });
}

export function useVoiceTenants() {
  return useQuery({ queryKey: [...ROOT, 'tenants'], queryFn: () => voice.adminListTenants() });
}

export function useVoiceNodes() {
  return useQuery({ queryKey: [...ROOT, 'nodes'], queryFn: () => voice.adminListNodes(), refetchInterval: 15_000 });
}

export function useVoiceAudit() {
  return useQuery({ queryKey: [...ROOT, 'audit'], queryFn: () => voice.adminListAudit({ limit: 50 }), refetchInterval: 30_000 });
}

/** Calls in progress, asked every few seconds while the page is open, and the most recent ones. */
export function useVoiceCalls(live: boolean) {
  return useQuery({ queryKey: [...ROOT, 'calls', live], queryFn: () => voice.adminListCalls({ live, limit: 50 }), refetchInterval: live ? 5_000 : 30_000 });
}

export function useVoiceBans() {
  return useQuery({ queryKey: [...ROOT, 'bans'], queryFn: () => voice.adminListBans(), refetchInterval: 15_000 });
}

export function useVoiceApplications() {
  return useQuery({ queryKey: [...ROOT, 'applications'], queryFn: () => voice.adminListApplications(), refetchInterval: 30_000 });
}

export function useVoiceCarriers() {
  return useQuery({ queryKey: [...ROOT, 'carriers'], queryFn: () => voice.adminListCarriers(), refetchInterval: 15_000 });
}

export function useVoiceInventory() {
  return useQuery({ queryKey: [...ROOT, 'inventory'], queryFn: () => voice.adminListInventory() });
}

export function useVoiceReadiness() {
  return useQuery({ queryKey: [...ROOT, 'readiness'], queryFn: () => voice.adminGetReadiness(), refetchInterval: 30_000 });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ROOT });
}

export function useUpdateVoiceSettings() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: (req: Parameters<typeof voice.adminUpdateSettings>[0]) => voice.adminUpdateSettings(req),
    onSettled: done
  });
}

export function useKillVoice() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (reason: string) => voice.adminKill(reason), onSettled: done });
}

export function useUpdateVoiceTenant() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: (v: { organisationId: string } & Parameters<typeof voice.adminUpdateTenant>[1]) => {
      const { organisationId, ...req } = v;
      return voice.adminUpdateTenant(organisationId, req);
    },
    onSettled: done
  });
}

export function useEndVoiceCall() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (id: string) => voice.adminEndCall(id), onSettled: done });
}

export function useLiftVoiceBan() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (id: string) => voice.adminLiftBan(id), onSettled: done });
}

export function useApproveVoiceApplication() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (v: { id: string; inventoryId: string }) => voice.adminApproveApplication(v.id, v.inventoryId), onSettled: done });
}

export function useRejectVoiceApplication() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (v: { id: string; reason: string }) => voice.adminRejectApplication(v.id, v.reason), onSettled: done });
}

export function useSaveVoiceCarrier() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: (v: { id?: string; input: voice.VoiceCarrierInput }) => (v.id ? voice.adminUpdateCarrier(v.id, v.input) : voice.adminCreateCarrier(v.input)),
    onSettled: done
  });
}

export function useDeleteVoiceCarrier() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (id: string) => voice.adminDeleteCarrier(id), onSettled: done });
}

export function useAddVoiceInventory() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (input: Parameters<typeof voice.adminAddInventory>[0]) => voice.adminAddInventory(input), onSettled: done });
}

export function useUpdateVoiceInventory() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (v: { id: string; retired: boolean }) => voice.adminUpdateInventory(v.id, { retired: v.retired }), onSettled: done });
}
